import { Dialog } from '@angular/cdk/dialog';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ReauthDialogComponent, ReauthDialogService, ReauthInput } from '../../ui';
import { parseProblem, problemMessage } from '../http/api-problem';
import { PasskeyService, PasskeyUnsupportedError, isPasskeyCancelled } from './passkey.service';

/** Risposta di `POST /api/auth/reauthenticate` quando manca il codice del secondo passaggio. */
interface ReauthenticateResponse {
  readonly status?: string;
}

type VerifyOutcome =
  { readonly ok: true } | { readonly codeRequired: true } | { readonly message: string };

interface SecuritySummary {
  readonly totp?: { readonly enabled?: boolean } | null;
  readonly passkeyCount?: number;
}

/** Cosa offrire nella finestra: codice dell'app e passkey. */
interface ReauthOptions {
  readonly totpEnabled: boolean;
  readonly passkeySupported: boolean;
}

/**
 * Conferma dell'identità per le operazioni sensibili (research R-10, FR-030…FR-031): apre la
 * finestra `bs-reauth-dialog` e verifica la scelta con `POST /api/auth/reauthenticate`. Più
 * richieste respinte insieme condividono la stessa finestra.
 */
@Injectable({ providedIn: 'root' })
export class ReauthenticationService {
  private readonly http = inject(HttpClient);
  private readonly dialogs = inject(ReauthDialogService);
  private readonly dialog = inject(Dialog);
  private readonly passkeys = inject(PasskeyService);
  private pending: Promise<boolean> | null = null;

  /** `true` se l'utente ha confermato l'identità, `false` se ha annullato. */
  confirm(): Promise<boolean> {
    this.pending ??= this.run().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  private async run(): Promise<boolean> {
    const options = await this.options();
    let totpEnabled = options.totpEnabled;
    for (;;) {
      // Il server può chiedere il codice anche se il riepilogo non lo diceva (verifica appena
      // attivata altrove): la finestra si riapre con il campo del codice.
      let needsCode = false;
      const confirmed = await this.dialogs.open(
        { totpEnabled, passkeySupported: options.passkeySupported },
        async (input) => {
          const outcome = await this.verify(input);
          if ('message' in outcome) {
            return outcome.message;
          }
          if ('codeRequired' in outcome) {
            if (totpEnabled) {
              return "Inserisci anche il codice dell'app di autenticazione.";
            }
            needsCode = true;
            this.closeDialog();
            return "Serve anche il codice dell'app di autenticazione.";
          }
          return null;
        },
      );
      if (needsCode) {
        totpEnabled = true;
        continue;
      }
      return confirmed;
    }
  }

  /**
   * `{ ok: true }` se l'identità è confermata, `codeRequired` se il server chiede anche il codice,
   * altrimenti il messaggio da mostrare.
   */
  private async verify(input: ReauthInput): Promise<VerifyOutcome> {
    if (input.kind === 'passkey') {
      return this.verifyPasskey();
    }
    const body: Record<string, string> = { password: input.password };
    if (input.code) body['code'] = input.code;
    if (input.recoveryCode) body['recoveryCode'] = input.recoveryCode;
    try {
      const response = await firstValueFrom(
        this.http.post<ReauthenticateResponse | null>('/api/auth/reauthenticate', body),
        { defaultValue: null },
      );
      return response?.status === 'second_factor_required' ? { codeRequired: true } : { ok: true };
    } catch (error: unknown) {
      return { message: reauthErrorMessage(error) };
    }
  }

  private async verifyPasskey(): Promise<VerifyOutcome> {
    try {
      await this.passkeys.reauthenticate();
      return { ok: true };
    } catch (error: unknown) {
      if (isPasskeyCancelled(error)) {
        return { message: 'La passkey non è stata usata. Riprova oppure usa la password.' };
      }
      if (error instanceof PasskeyUnsupportedError) {
        return { message: 'Questo browser non supporta le passkey: usa la password.' };
      }
      return { message: reauthErrorMessage(error) };
    }
  }

  /**
   * Codice dell'app se la verifica è attiva; passkey se il browser le supporta e l'utente ne ha
   * almeno una.
   */
  private async options(): Promise<ReauthOptions> {
    try {
      const summary = await firstValueFrom(this.http.get<SecuritySummary>('/api/me/security'));
      return {
        totpEnabled: summary?.totp?.enabled === true,
        passkeySupported: this.passkeys.isSupported() && (summary?.passkeyCount ?? 0) > 0,
      };
    } catch {
      return { totpEnabled: false, passkeySupported: false };
    }
  }

  private closeDialog(): void {
    const ref = this.dialog.openDialogs.find(
      (item) => item.componentInstance instanceof ReauthDialogComponent,
    );
    ref?.close(false);
  }
}

/** Il messaggio italiano per un errore della conferma dell'identità. */
export function reauthErrorMessage(error: unknown): string {
  const problem = parseProblem(error);
  switch (problem.code) {
    case 'invalid_credentials':
      return 'La password non è corretta.';
    case 'passkey_invalid':
      return 'La passkey non è stata riconosciuta. Riprova oppure usa la password.';
    case 'validation_failed':
      return (
        problem.errors['password']?.[0] ?? problem.errors['code']?.[0] ?? problemMessage(problem)
      );
    default:
      return problemMessage(problem);
  }
}
